import * as THREE from 'three';
import { CLASSICAL, INDUSTRIAL, MODERN } from '../materials';
import { Beam, HexPad, levelEntry, type ModelProps, Pole, Smoke } from '../parts';
import { Artillery } from '../units/industrial';

const { PLANK } = CLASSICAL;
const { BRICK, DARK_BRICK, IRON, STEEL: GIRDER, SOOT, SLATE, COBBLE, TYRE, DRUM } = INDUSTRIAL;
const { CONCRETE, DARK_CONCRETE, GLASS, ASPHALT, ROAD_LINE, STEEL } = MODERN;

/**
 * One sawtooth roof tooth, 1 × 1 × 1: a right-angled prism along x with its
 * glazed vertical face at the front (+z) and its slope falling to the back.
 * Meshes scale it to the hall.
 */
function toothGeometry(): THREE.BufferGeometry {
  const profile = new THREE.Shape([
    new THREE.Vector2(0, 0),
    new THREE.Vector2(1, 0),
    new THREE.Vector2(0, 1),
  ]);
  return new THREE.ExtrudeGeometry(profile, { depth: 1, bevelEnabled: false })
    .rotateY(Math.PI / 2)
    .translate(-0.5, 0, 0.5);
}

const G = {
  box: new THREE.BoxGeometry(1, 1, 1),
  tooth: toothGeometry(),
  chimney: new THREE.CylinderGeometry(0.03, 0.042, 1, 8),
  chimneyCap: new THREE.CylinderGeometry(0.038, 0.038, 0.03, 8),
  crate: new THREE.BoxGeometry(0.08, 0.08, 0.08),
  hook: new THREE.BoxGeometry(0.05, 0.04, 0.05),
  roofUnit: new THREE.BoxGeometry(0.1, 0.05, 0.08),
  // Vehicle yard: a lorry (cab, bed, wheels) and oil drums.
  cab: new THREE.BoxGeometry(0.1, 0.09, 0.08),
  bed: new THREE.BoxGeometry(0.11, 0.05, 0.18),
  tyre: new THREE.CylinderGeometry(0.03, 0.03, 0.02, 8).rotateZ(Math.PI / 2),
  drum: new THREE.CylinderGeometry(0.028, 0.028, 0.07, 8),
  bayLine: new THREE.BoxGeometry(0.012, 0.004, 0.22),
  floodlight: new THREE.BoxGeometry(0.08, 0.03, 0.03),
  canopy: new THREE.BoxGeometry(0.46, 0.02, 0.22),
};

type Hall = {
  /** Width (x), wall height and depth (z); the hall's back sits at `z - d / 2`. */
  w: number;
  h: number;
  d: number;
  z: number;
  walls: THREE.Material;
  /** Sawtooth teeth front to back (0: a flat roof with rooftop units). */
  teeth: number;
  roof: THREE.Material;
};

type FactoryLook = {
  hall: Hall;
  /** Chimney material and height (twin stacks behind the hall, smoking). */
  chimneys: { material: THREE.Material; height: number };
  /** Loading doors across the hall's front. */
  doors: number;
  /** A gantry crane over the front yard, spanning x from `x0` to `x1`. */
  crane: { x0: number; x1: number } | null;
  /** Glow strips round the hall and a glazed office block at the front left. */
  plant: boolean;
  /** The front yard's paving. */
  yard: THREE.Material;
};

/**
 * Factory look by level: entry i is level i + 1, levels past the end use the
 * last. L1 a brick sawtooth hall, L2 bigger with a gantry crane, L3 a modern
 * assembly plant.
 */
const FACTORY_LEVELS: readonly FactoryLook[] = [
  {
    hall: { w: 0.6, h: 0.2, d: 0.4, z: -0.12, walls: BRICK, teeth: 3, roof: SLATE },
    chimneys: { material: DARK_BRICK, height: 0.55 },
    doors: 2,
    crane: null,
    plant: false,
    yard: COBBLE,
  },
  {
    hall: { w: 0.74, h: 0.22, d: 0.46, z: -0.14, walls: BRICK, teeth: 4, roof: SLATE },
    chimneys: { material: DARK_BRICK, height: 0.62 },
    doors: 2,
    crane: { x0: -0.32, x1: 0.32 },
    plant: false,
    yard: COBBLE,
  },
  {
    hall: { w: 0.8, h: 0.28, d: 0.5, z: -0.15, walls: CONCRETE, teeth: 0, roof: DARK_CONCRETE },
    chimneys: { material: STEEL, height: 0.66 },
    doors: 3,
    crane: { x0: -0.12, x1: 0.38 },
    plant: true,
    yard: ASPHALT,
  },
];

const TOOTH_HEIGHT = 0.1;
const ROOF_UNITS = [-0.2, 0.05, 0.25];
const CHIMNEY_XS = [0.14, 0.28];
const CHIMNEY_Z = -0.46;
const DOOR = { w: 0.13, h: 0.14 };
const CRANE = { z: 0.34, height: 0.38 };
const YARD_FRONT = 0.5;

/** A box mesh of size `size` centred at `position`. */
function Block({
  size,
  position,
  material,
}: {
  size: [number, number, number];
  position: [number, number, number];
  material: THREE.Material;
}) {
  return <mesh geometry={G.box} material={material} position={position} scale={size} />;
}

/** The hall: walls, then a sawtooth roof with glazed faces or a flat roof with rooftop units. */
function FactoryHall({ hall }: { hall: Hall }) {
  const { w, h, d, z, walls, teeth, roof } = hall;
  const tooth = d / Math.max(teeth, 1);
  const back = z - d / 2;
  return (
    <>
      <Block size={[w, h, d]} position={[0, h / 2, z]} material={walls} />
      {teeth > 0
        ? Array.from({ length: teeth }, (_, i) => {
            const tz = back + tooth * (i + 0.5);
            return (
              <group key={tz}>
                <mesh
                  geometry={G.tooth}
                  material={roof}
                  position={[0, h, tz]}
                  scale={[w, TOOTH_HEIGHT, tooth]}
                />
                <Block
                  size={[w * 0.92, TOOTH_HEIGHT * 0.7, 0.006]}
                  position={[0, h + TOOTH_HEIGHT * 0.45, tz + tooth / 2 + 0.003]}
                  material={GLASS}
                />
              </group>
            );
          })
        : [
            <Block
              key="roof"
              size={[w + 0.02, 0.02, d + 0.02]}
              position={[0, h + 0.01, z]}
              material={roof}
            />,
            ...ROOF_UNITS.map((x) => (
              <mesh
                key={x}
                geometry={G.roofUnit}
                material={STEEL}
                position={[x, h + 0.045, z - 0.05]}
              />
            )),
          ]}
    </>
  );
}

/** A gantry crane: steel legs at both ends, a girder, and a team hook block lowering a crate. */
function GantryCrane({ accent, x0, x1 }: { accent: THREE.Material; x0: number; x1: number }) {
  const top = CRANE.height;
  const hookX = x0 + (x1 - x0) * 0.6;
  return (
    <group position={[0, 0, CRANE.z]}>
      {[x0, x1].map((x) => (
        <group key={x}>
          <Beam from={[x, 0, -0.08]} to={[x, top, 0]} material={GIRDER} thickness={0.022} />
          <Beam from={[x, 0, 0.08]} to={[x, top, 0]} material={GIRDER} thickness={0.022} />
        </group>
      ))}
      <Block
        size={[x1 - x0 + 0.04, 0.04, 0.05]}
        position={[(x0 + x1) / 2, top, 0]}
        material={IRON}
      />
      <Block size={[0.07, 0.035, 0.07]} position={[hookX, top - 0.035, 0]} material={SOOT} />
      <Beam from={[hookX, top - 0.05, 0]} to={[hookX, 0.19, 0]} material={SOOT} thickness={0.008} />
      <mesh geometry={G.hook} material={accent} position={[hookX, 0.17, 0]} />
      <mesh geometry={G.crate} material={PLANK} position={[hookX, 0.11, 0]} />
    </group>
  );
}

/**
 * A factory: a sawtooth-roofed brick hall with glazed roof lights, team
 * loading doors across its front, twin smoking chimneys behind and a paved
 * yard with crates in front. L2 enlarges the hall and adds a gantry crane
 * over the yard; L3 rebuilds it as a modern assembly plant: a flat-roofed
 * concrete hall banded with glow strips, steel stacks, three doors and a
 * glazed office block.
 */
export function Factory({ accent, glow, level }: ModelProps) {
  const look = levelEntry(FACTORY_LEVELS, level);
  const { hall } = look;
  const front = hall.z + hall.d / 2;
  const doorGap = hall.w / (look.doors + 1);
  const stacks = CHIMNEY_XS.map((x): [number, number, number] => [
    x,
    look.chimneys.height,
    CHIMNEY_Z,
  ]);
  return (
    <>
      <FactoryHall hall={hall} />
      {Array.from({ length: look.doors }, (_, i) => {
        const x = -hall.w / 2 + doorGap * (i + 1);
        return (
          <Block
            key={x}
            size={[DOOR.w, DOOR.h, 0.012]}
            position={[x, DOOR.h / 2, front + 0.006]}
            material={accent}
          />
        );
      })}
      {stacks.map(([x, h, z]) => (
        <group key={x} position={[x, 0, z]}>
          <mesh
            geometry={G.chimney}
            material={look.chimneys.material}
            position={[0, h / 2, 0]}
            scale={[1, h, 1]}
          />
          <mesh geometry={G.chimneyCap} material={SOOT} position={[0, h, 0]} />
        </group>
      ))}
      <Smoke stacks={stacks} />
      <Block
        size={[hall.w, 0.02, YARD_FRONT - front]}
        position={[0, 0.01, (front + YARD_FRONT) / 2]}
        material={look.yard}
      />
      {look.crane ? (
        <GantryCrane accent={accent} {...look.crane} />
      ) : (
        [0.2, 0.28].map((x) => (
          <mesh key={x} geometry={G.crate} material={PLANK} position={[x, 0.06, 0.32 + x * 0.2]} />
        ))
      )}
      {look.plant && (
        <>
          {[0.45, 0.85].map((f) => (
            <Block
              key={f}
              size={[hall.w + 0.006, 0.018, hall.d + 0.006]}
              position={[0, hall.h * f, hall.z]}
              material={glow}
            />
          ))}
          <group position={[-0.3, 0, 0.25]}>
            <Block size={[0.18, 0.24, 0.16]} position={[0, 0.12, 0]} material={GLASS} />
            <Block size={[0.2, 0.02, 0.18]} position={[0, 0.25, 0]} material={accent} />
          </group>
        </>
      )}
    </>
  );
}

/** A lorry facing +z: team cab, iron bed, four tyres. */
function Lorry({ accent }: { accent: THREE.Material }) {
  return (
    <>
      <mesh geometry={G.cab} material={accent} position={[0, 0.075, 0.11]} />
      <mesh geometry={G.bed} material={IRON} position={[0, 0.055, -0.04]} />
      {[-0.06, 0.06].flatMap((x) =>
        [-0.08, 0.1].map((z) => (
          <mesh key={`${x}:${z}`} geometry={G.tyre} material={TYRE} position={[x, 0.03, z]} />
        ))
      )}
    </>
  );
}

type Spot = { x: number; z: number; yaw: number };

type YardLook = {
  pad: THREE.Material;
  lorries: Spot[];
  /** A field gun parked in the yard. */
  gun: Spot | null;
  crates: [number, number][];
  drums: [number, number][];
  /** Painted parking bays, a steel canopy and a floodlight (L3). */
  modern: boolean;
};

const LORRY_A: Spot = { x: -0.24, z: 0.02, yaw: 0.3 };
const LORRY_B: Spot = { x: 0.02, z: -0.3, yaw: Math.PI / 2 };
const CRATES: [number, number][] = [
  [0.3, 0.3],
  [0.38, 0.22],
];
const MORE_CRATES: [number, number][] = [...CRATES, [0.34, 0.26], [0.2, -0.36]];
const DRUMS: [number, number][] = [
  [-0.36, 0.36],
  [-0.3, 0.42],
  [-0.42, 0.3],
];
// Parking bays across the front, the canopy over the back row.
const BAYS = [-0.33, -0.11, 0.11, 0.33];

/** Extra-tile look by level (tiles exist from L2; L1 is kept so the table indexes by level). */
const YARD_LEVELS: readonly YardLook[] = [
  { pad: COBBLE, lorries: [LORRY_A], gun: null, crates: CRATES, drums: [], modern: false },
  { pad: COBBLE, lorries: [LORRY_A], gun: null, crates: MORE_CRATES, drums: DRUMS, modern: false },
  {
    pad: ASPHALT,
    lorries: [LORRY_A, LORRY_B],
    gun: { x: 0.1, z: 0.05, yaw: 0 },
    crates: MORE_CRATES,
    drums: DRUMS,
    modern: true,
  },
];

/**
 * A grown factory's extra tile: a vehicle yard with a team-cabbed lorry and
 * crates; L2 adds more crates and oil drums, L3 paves it in asphalt with
 * parking bays, a second lorry under a steel canopy, a parked field gun and a
 * floodlight.
 */
export function FactoryTile({ accent, glow, level }: ModelProps) {
  const look = levelEntry(YARD_LEVELS, level);
  return (
    <>
      <HexPad material={look.pad} />
      <group position={[0, 0.03, 0]}>
        {look.lorries.map((s) => (
          <group key={s.x} position={[s.x, 0, s.z]} rotation={[0, s.yaw, 0]}>
            <Lorry accent={accent} />
          </group>
        ))}
        {look.gun && (
          <group position={[look.gun.x, 0, look.gun.z]} rotation={[0, look.gun.yaw, 0]} scale={0.8}>
            <Artillery accent={accent} glow={glow} level={1} />
          </group>
        )}
        {look.crates.map(([x, z]) => (
          <mesh key={`${x}:${z}`} geometry={G.crate} material={PLANK} position={[x, 0.04, z]} />
        ))}
        {look.drums.map(([x, z]) => (
          <mesh key={`${x}:${z}`} geometry={G.drum} material={DRUM} position={[x, 0.035, z]} />
        ))}
        {look.modern && (
          <>
            {BAYS.map((x) => (
              <mesh key={x} geometry={G.bayLine} material={ROAD_LINE} position={[x, 0.002, 0.34]} />
            ))}
            <group position={[0.02, 0, -0.3]}>
              {[-0.21, 0.21].map((x) => (
                <Pole key={x} length={0.2} position={[x, 0.1, 0.09]} material={STEEL} />
              ))}
              <mesh geometry={G.canopy} material={accent} position={[0, 0.2, 0]} />
            </group>
            <group position={[-0.46, 0, -0.12]}>
              <Pole length={0.44} position={[0, 0.22, 0]} material={STEEL} />
              <mesh geometry={G.floodlight} material={glow} position={[0.02, 0.44, 0]} />
            </group>
          </>
        )}
      </group>
    </>
  );
}
